import { useEffect, useMemo } from "react"
import { Grid } from "@mui/material"
import { debounce } from "lodash-es"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { MeetingServiceConfig, MainOrderFormikRef, MeetingServiceTabValues } from "../form/formTypes"
import {
    SelectedMainUserState,
    SelectedPriceListItem,
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
} from "../../createOrderTypes"
import { CreateOrderDialogDrawer } from "../drawer/CreateOrderDialogDrawer"
import { DrawerContent } from "../drawer/DrawerContent"
import { CreateOrderType } from "../../helpers"
import { ShopList } from "../shop/ShopList"

const useStyles = makeStyles()(() => ({
    contentSlide: {
        height: "100%",
    },
}))

type MeetingServiceFormContentProps = Readonly<{
    user: SelectedMainUserState
    handleClose: () => void
    dialogType: CreateOrderDialogType
    handleOpenSelectPriceListsDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    isDrawerOpen: boolean
    orderType: CreateOrderType
    meetingServiceConfiguration?: MeetingServiceConfig
    outerFormik: MainOrderFormikRef
    mode: CreateOrderDialogMode
}>

export function MeetingServiceFormContent({
    user,
    handleClose,
    dialogType,
    handleOpenSelectPriceListsDialog,
    meetingServiceConfiguration,
    flowConfig,
    isDrawerOpen,
    orderType,
    outerFormik,
    mode,
}: MeetingServiceFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<MeetingServiceTabValues>()
    const priceList: SelectedPriceListItem = values["priceList"]

    const debouncedSyncValues = useMemo(
        () =>
            debounce((newValues: MeetingServiceTabValues) => {
                Object.entries(newValues).forEach(([key, value]) => {
                    outerFormik?.current?.setFieldValue(key, value)
                })
            }, 400),
        [outerFormik],
    )

    useEffect(() => {
        const outerPriceList = outerFormik.current?.values["priceList"]

        const outerId = outerPriceList?.priceListId
        const localId = priceList?.priceListId

        if (outerId && outerId !== localId) {
            setFieldValue("priceList", outerPriceList)
        }
    }, [outerFormik.current?.values["priceList"]?.priceListId])

    useEffect(() => {
        debouncedSyncValues(values)
    }, [values, debouncedSyncValues])

    useEffect(() => {
        return () => {
            debouncedSyncValues.cancel()
        }
    }, [debouncedSyncValues])

    return (
        <>
            <Grid container className={classes.contentSlide}>
                {priceList && (
                    <ShopList
                        priceList={priceList}
                        meetingServiceConfiguration={meetingServiceConfiguration}
                        handleCloseMainDialog={handleClose}
                        flowConfig={flowConfig}
                        handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                        dialogType={dialogType}
                        user={user}
                    />
                )}
            </Grid>
            {meetingServiceConfiguration && (
                <CreateOrderDialogDrawer open={isDrawerOpen}>
                    <DrawerContent
                        meetingServiceConfiguration={meetingServiceConfiguration}
                        dialogType={dialogType}
                        user={user}
                        orderType={orderType}
                        mode={mode}
                    />
                </CreateOrderDialogDrawer>
            )}
        </>
    )
}
